import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { OTPUnlimitedType } from "../../../../types/types";
import Button from "../../../../components/ui/Button";
import { generateCodesStories, generateCodes, getUnlimitedToken } from "../../../../api";
import NumberInput from "../../../../components/common/NumberInput";
import TitleSubtitle from "../../../../components/common/TitleSubtitle";
import { useTrackCreation } from "../../../../context/TrackCreationContext";
import styles from "./QRCodes.module.css";

const QRCodes: React.FC = () => {
    const { trackData } = useTrackCreation(); // assume trackData is of type TrackDataType
    const [codeNumber, setCodeNumber] = useState<number>(1);
    const [OTPUnlimited, setOTPUnlimited] = useState<OTPUnlimitedType | null>(null);
    const { t: rawT } = useTranslation();
    const t = rawT as any;

    const DownloadQRCode = (): void => {
        if (OTPUnlimited && OTPUnlimited.url) {
            window.open(OTPUnlimited.url, "_blank"); // Open in a new tab or window
        }
    };

    const DownloadLink = (): void => {
        if (OTPUnlimited && OTPUnlimited.urlString) {
            window.open(OTPUnlimited.urlString, "_blank"); // Open in a new tab or window
        }
    };

    const DownloadCSV = (): void => {
        if (OTPUnlimited && OTPUnlimited.urlCsv) {
            window.open(OTPUnlimited.urlCsv, "_blank"); // Open in a new tab or window
        }
    };

    const DownloadQRCodesStoriesMuseum = async (): Promise<void> => {
        const token = localStorage.getItem("token");
        const trackId = trackData?.trackId;
        if (!token || !trackId) return;
        const otptoken = await generateCodesStories({ token, trackId });
        if (otptoken) {
            window.open(otptoken.toString(), "_blank");
        }
    };

    const DownloadQRCodes = async (): Promise<void> => {
        const token = localStorage.getItem("token");
        const trackId = trackData?.trackId;
        if (!token || !trackId) return;
        const otptoken = await generateCodes({ token, trackId, numberTokens: codeNumber });
        if (otptoken && otptoken.url) {
            window.open(otptoken.url, "_blank");
        }
    };

    const DownloadLinks = async (): Promise<void> => {
        const token = localStorage.getItem("token");
        const trackId = trackData?.trackId;
        if (!token || !trackId) return;
        const otptoken = await generateCodes({ token, trackId, numberTokens: codeNumber });
        if (otptoken && otptoken.urlString) {
            window.open(otptoken.urlString, "_blank");
        }
    };

    const DownloadCSVs = async (): Promise<void> => {
        const token = localStorage.getItem("token");
        const trackId = trackData?.trackId;
        if (!token || !trackId) return;
        const otptoken = await generateCodes({ token, trackId, numberTokens: codeNumber });
        if (otptoken && otptoken.urlCsv) {
            window.open(otptoken.urlCsv, "_blank");
        }
    };

    const getTokenUnlimited = async (): Promise<void> => {
        const token = localStorage.getItem("token");
        const trackId = trackData?.trackId;
        if (!token || !trackId) return;
        const otptoken = await getUnlimitedToken({ token, trackId });
        setOTPUnlimited(otptoken);
    };

    useEffect(() => {
        if (trackData && trackData.trackId) {
            getTokenUnlimited();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [trackData?.trackId]);

    const outlineBtn = styles.qrBtn;

    return (
        <div className={`step-container ${styles.root}`}>
            <TitleSubtitle title={t("qrCodesAndActivationLinks")} subtitle={t("generateHereQRCodeOrAccessLinks")} />

            <div className={styles.qrRow}>
                <div className={styles.qrLabel}>
                    <span className={styles.qrH}>{t("unlimitedAccess")}</span>
                </div>
                <div className={styles.qrBtns}>
                    <Button label={t("downloadQrCode")} className={outlineBtn} onClick={DownloadQRCode} />
                    <Button label={t("downloadLink")} className={outlineBtn} onClick={DownloadLink} />
                    <Button label={t("downloadCsv")} className={outlineBtn} onClick={DownloadCSV} />
                </div>
            </div>

            <div className={styles.qrRow}>
                <div className={styles.qrLabel}>
                    <span className={styles.qrH}>{t("oneTimeAccess")}</span>
                    <span className={styles.qrHelp}>
                        {t("indicateHereTheNumberOfUniqueActivationCodeYouWantToDownload")}
                    </span>
                </div>
                <div className={styles.qrBtns}>
                    <NumberInput
                        placeholder={t("writeANumber")}
                        value={codeNumber}
                        size="small"
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                            setCodeNumber(Number(e.target.value));
                        }}
                    />
                    <Button label={t("downloadQrCodes")} className={outlineBtn} onClick={DownloadQRCodes} />
                    <Button label={t("downloadLinks")} className={outlineBtn} onClick={DownloadLinks} />
                    <Button label={t("downloadCsvs")} className={outlineBtn} onClick={DownloadCSVs} />
                </div>
            </div>

            {trackData && trackData.type === "iduniquemusee" && trackData.museumSearchQrcode === true && (
                <div className={styles.qrRow}>
                    <div className={styles.qrLabel}>
                        <span className={styles.qrH}>{t("museumQrCodeStories")}</span>
                    </div>
                    <div className={styles.qrBtns}>
                        <Button
                            label={t("downloadQrCodes")}
                            className={outlineBtn}
                            onClick={DownloadQRCodesStoriesMuseum}
                        />
                    </div>
                </div>
            )}
        </div>
    );
};

export default QRCodes;
